/**
 * Agents shared-view READ bootstrap — RO-3-00f / #44473.
 *
 * Port of `AgentsRunsView` in `.../_agents/views.tsx` (cause C — blocked on
 * RO-2-11 actions which are now endpoints). The view needs one round-trip for
 * the stat strip (recent window + today telemetry) plus the spawnable roles and
 * the first page of the keyset feed. Goal list/detail/create already live on
 * `agentGoals.*`; worker spawn on `agents.workerSpawn`; the feed alone is
 * `agentRuns.feed`. This op is the remaining aggregate the runs body renders.
 */

import { z } from "zod";
import type { Operation } from "../types";
import { keysetSchema } from "./_common";
import { workerRunFeedRowSchema, type WorkerRunFeedRowDto } from "./agent-runs";

const role = z.object({
  name: z.string(),
  description: z.string(),
});

const firstPageSchema = keysetSchema(workerRunFeedRowSchema);

const runsBootstrapResponse = z.object({
  /** Recent window (last 100) for the at-a-glance strip. */
  recent: z.array(workerRunFeedRowSchema),
  /** First page of the keyset feed (seed for WorkerRunsFeedPaged). */
  firstPage: firstPageSchema,
  today: z.object({
    cost_usd: z.number(),
    tokens: z.number(),
  }),
  roles: z.array(role),
  counts: z.object({
    running: z.number(),
    failed: z.number(),
    budgetExceeded: z.number(),
  }),
});
export type AgentsRunsBootstrapResponse = z.infer<typeof runsBootstrapResponse>;
export type { WorkerRunFeedRowDto };

const runsBootstrapOp: Operation<void, void, void, AgentsRunsBootstrapResponse> = {
  method: "GET",
  path: () => "/api/agents/views/runs",
  response: runsBootstrapResponse,
};

/* ── Overview bootstrap (Agents command center at `/agents`) ──────────────── */

const suggestionItem = z.object({
  id: z.string(),
  kind: z.enum(["flow", "agent", "tool"]),
  title: z.string(),
  rationale: z.string(),
  state: z.enum(["optimize", "draft", "suggested"]).optional(),
  evidence: z.string().optional(),
  actionHref: z.string().optional(),
});

const overviewTask = z.object({
  id: z.number(),
  uid: z.string().optional(),
  title: z.string(),
  status: z.string(),
  priority: z.string().optional(),
  project_slug: z.string().optional(),
  project_name: z.string().optional(),
  section_slug: z.string().optional(),
  section_name: z.string().optional(),
  assignee: z.string().nullable().optional(),
  assignee_label: z.string().nullable().optional(),
  goal_id: z.number().nullable().optional(),
  goal_title: z.string().nullable().optional(),
  updated_at: z.string().optional(),
});

const fleetGoal = z.object({
  goalId: z.number(),
  title: z.string(),
  managerActive: z.boolean(),
  ship: z.number(),
  review: z.number(),
  integrate: z.number(),
  integrated: z.number(),
  awaitingHuman: z.number(),
  promotable: z.boolean(),
});

const overviewResponse = z.object({
  roles: z.array(role),
  statusCounts: z.record(z.string(), z.number()).nullable(),
  tasks: z.array(overviewTask),
  suggestions: z.array(suggestionItem),
  usageSummary: z.string(),
  fleet: z
    .object({
      liveLanes: z.number(),
      goals: z.array(fleetGoal),
      hardening: z
        .object({
          today: z.record(z.string(), z.number()),
        })
        .optional(),
    })
    .nullable(),
  emptyMessage: z.string(),
});
export type AgentsOverviewResponse = z.infer<typeof overviewResponse>;
export type AgentsOverviewTask = z.infer<typeof overviewTask>;

const overviewQuery = z.object({
  /** Comma-separated task statuses; default active preset server-side. */
  status: z.string().optional(),
});

const overviewOp: Operation<void, z.infer<typeof overviewQuery>, void, AgentsOverviewResponse> = {
  method: "GET",
  path: () => "/api/agents/views/overview",
  query: overviewQuery,
  response: overviewResponse,
};

/* ── Lane operations console (`/agents/lanes`) ────────────────────────────── */

const laneLease = z.object({
  laneId: z.number(),
  pid: z.number().nullable(),
  alive: z.boolean(),
  state: z.enum(["live", "dead"]),
});

const laneTask = overviewTask.extend({
  ageLabel: z.string().optional(),
  href: z.string(),
});

const laneGoal = fleetGoal.extend({
  href: z.string(),
});

const laneWorktree = z.object({
  key: z.string(),
  branch: z.string(),
});

const lanesResponse = z.object({
  generatedAt: z.string(),
  summary: z.object({
    cell: z.string(),
    poolSize: z.number(),
    liveLanes: z.number(),
    leasedLanes: z.number(),
    freeLanes: z.number(),
    zombieLanes: z.number(),
    todo: z.number(),
    inProgress: z.number(),
    review: z.number(),
  }),
  leases: z.array(laneLease),
  zombieLaneIds: z.array(z.number()),
  worktrees: z.array(laneWorktree),
  goals: z.array(laneGoal),
  inProgress: z.array(laneTask),
  review: z.array(laneTask),
  todo: z.array(laneTask),
  notes: z.array(z.string()),
});
export type AgentsLanesResponse = z.infer<typeof lanesResponse>;
export type AgentsLaneTask = z.infer<typeof laneTask>;

const lanesOp: Operation<void, void, void, AgentsLanesResponse> = {
  method: "GET",
  path: () => "/api/agents/views/lanes",
  response: lanesResponse,
};

export const agentsViews = {
  runsBootstrap: runsBootstrapOp,
  overview: overviewOp,
  lanes: lanesOp,
};
